import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { useSaveIndicator } from '../../hooks/useSaveIndicator';
import { SaveToast } from '../../components/SaveToast';

// Fallback when the Worker is unreachable. Same fields the Worker will return
// (snake_case, matching the brand_config.key column).
const MOCK_DEFAULTS = {
  voice:
    "F8M speaks with warmth and quiet authority. We're a foundation that takes its mission seriously but never takes itself too seriously. Our voice is professional but human — like a thoughtful friend who happens to be an expert. We use 'we' and 'our' to invite the reader into our work, never 'I' or 'us-vs-them' language. Polish is our primary tongue; English serves as a bridge to international partners and audiences.",
  tone:
    "LinkedIn: measured and substantive — speak to professionals about heritage policy, partnerships, and sector developments. Sentences can be longer; data and citations welcome.\n\nInstagram: warmer and more visual — let the imagery lead, captions support. Use line breaks for rhythm. Emoji sparingly but meaningfully.\n\nFacebook: conversational and community-oriented — invite participation, ask questions, celebrate the people behind the work.",
  language_rules:
    "Always speak from 'we' and 'our' — F8M is a collective. Never 'I' (no individual voice attributed unless quoting a named person). Avoid 'you should' framing — prefer 'we believe' or 'we've found'. Partner language: name partners explicitly and tag them. Beneficiary language: dignified, never patronising; centre their agency.",
};

const FIELDS = [
  { key: 'voice',          labelKey: 'brand.voiceTone.voice',       hintKey: 'brand.voiceTone.voiceHint' },
  { key: 'tone',           labelKey: 'brand.voiceTone.tone',        hintKey: 'brand.voiceTone.toneHint' },
  { key: 'language_rules', labelKey: 'brand.voiceTone.perspective', hintKey: 'brand.voiceTone.perspectiveHint' },
];

export function VoiceToneTab() {
  const { t } = useTranslation();
  const { status, setSaving, flashSaved, flashError } = useSaveIndicator();

  const [values, setValues] = useState({ voice: '', tone: '', language_rules: '' });
  const [savedSnapshot, setSavedSnapshot] = useState({ voice: '', tone: '', language_rules: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  // Bumping `loadKey` re-runs the fetch effect — used by "Reset to Default".
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    api.brand
      .getAll()
      .then((data) => {
        if (cancelled) return;
        const next = {
          voice: data?.voice ?? '',
          tone: data?.tone ?? '',
          language_rules: data?.language_rules ?? '',
        };
        setValues(next);
        setSavedSnapshot(next);
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[brand] API unreachable, using mock data:', err.message);
        setValues({ ...MOCK_DEFAULTS });
        setSavedSnapshot({ ...MOCK_DEFAULTS });
        setUsingMockData(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [loadKey]);

  function update(key, value) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    // Send only fields that diverged from the server's last confirmed snapshot.
    const dirty = {};
    for (const field of FIELDS) {
      if (values[field.key] !== savedSnapshot[field.key]) {
        dirty[field.key] = values[field.key];
      }
    }
    if (Object.keys(dirty).length === 0) {
      flashSaved();
      return;
    }
    setSaving();
    try {
      const updated = await api.brand.update(dirty);
      const next = {
        voice: updated?.voice ?? values.voice,
        tone: updated?.tone ?? values.tone,
        language_rules: updated?.language_rules ?? values.language_rules,
      };
      // Don't overwrite `values` — the user may have kept typing while the
      // request was in flight. Just update the snapshot we compare against.
      setSavedSnapshot(next);
      flashSaved();
    } catch (err) {
      console.error('[brand] save failed:', err);
      flashError();
    }
  }

  function handleReset() {
    setLoadKey((k) => k + 1);
  }

  const isDirty = FIELDS.some((f) => values[f.key] !== savedSnapshot[f.key]);

  if (isLoading) {
    return (
      <div className="empty-state">
        <div className="empty-state__icon" aria-hidden="true">⏳</div>
        <p>{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="tab-page">
      {usingMockData && <div className="dev-notice">{t('events.loadError')}</div>}

      <div className="voice-form">
        {FIELDS.map((field) => (
          <label key={field.key} className="voice-field">
            <span className="voice-field__title">{t(field.labelKey)}</span>
            <span className="voice-field__hint">{t(field.hintKey)}</span>
            <textarea
              className="voice-field__textarea"
              value={values[field.key]}
              onChange={(e) => update(field.key, e.target.value)}
            />
          </label>
        ))}

        <div className="voice-form__actions">
          <button
            type="button"
            className="button button--primary"
            onClick={handleSave}
            disabled={!isDirty || status === 'saving'}
          >
            {t('brand.common.saveChanges')}
          </button>
          <button
            type="button"
            className="button button--ghost"
            onClick={handleReset}
            disabled={status === 'saving'}
          >
            {t('brand.common.resetToDefault')}
          </button>
        </div>
      </div>

      <SaveToast status={status} />
    </div>
  );
}
